"use client";

import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Divider, Checkbox } from "@mui/material";
import { useRouter } from "next/navigation";
import Spinner from "@/component/Shared/Spinner";

type Props = {
  authData: any;
  setAuthData: any;
  stepsHandler: any;
  isLoading: boolean;
  registerUserHandler: any;
};

const TermsAndServices = ({
  authData,
  setAuthData,
  stepsHandler,
  isLoading,
  registerUserHandler,
}: Props) => {
  const router = useRouter();

  const [errors, setErrors] = useState({
    acceptTermsOfServices: "",
  });

  const handleAgreementChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAuthData((prevState: any) => ({
      ...prevState,
      acceptTermsOfServices: event.target.checked,
    }));

    if (!event.target.checked) {
      setErrors((prevState) => ({
        ...prevState,
        acceptTermsOfServices: "Agree to terms and conditions is required.",
      }));

      return;
    }

    if (event.target.checked) {
      // Clear the error message as soon as the user starts typing
      setErrors((prevErrors) => ({
        ...prevErrors,
        acceptTermsOfServices: "",
      }));
    }
  };

  const prevStepHandler = () => {
    stepsHandler("userDataForm");
  };

  const nextStepHandler = () => {
    // Error Handling Code
    const newErrors = {
      acceptTermsOfServices: authData.acceptTermsOfServices
        ? ""
        : "Agree to terms & services is required.",
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((err) => err !== "");
    if (hasErrors) return;

    registerUserHandler();
  };

  useEffect(() => {
    // Scroll to top after TermsAndServices mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
        // overflowY: "auto",
        // maxHeight: "100vh",
        // paddingBottom: "80px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          // paddingX: "16px",
          marginTop: "-16px",
        }}
      >
        {/* // */}

        <div className="">
          <div className="mx-auto">
            {/* Title */}
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              WHITE LABEL SOFTWARE LICENSE AGREEMENT
            </h1>

            <p className="text-sm text-gray-600 mb-6">
              Effective Date: 02/21/26
            </p>

            <p className="text-sm text-gray-800 leading-relaxed mb-8">
              This Software License Agreement (“Agreement”) is entered into
              between MyServiceApp LLC (“MSA,” “we,” “us,” or “our”) and you,
              the independent contractor (DBA), Inc., or LLC licensing the
              Software (“Licensee,” “you,” or “your”). By creating an account or
              using the MSA software (the “Software”), you agree to be bound by
              this Agreement.
            </p>

            <Section title="1. The Software">
              MSA provides a subscription-based software-as-a-service (SaaS)
              platform that enables Licensees to manage and operate their
              services under their own trade names. The Software facilitates
              customer relationship management (CRM), centralizes customer data,
              streamlines sales, marketing, customer service, and commerce
              processes, and integrates analytics for forecasting and customer
              insights.
              <br />
              <br />
              MSA does not employ Licensees, own or operate equipment, act as a
              transportation carrier or provide services to Licensees’ clients,
              customers or end-users. All services are provided solely by
              Licensee and its employees and/or its subcontractors.
            </Section>

            <Section title="2. Relationship Between the Parties">
              Licensee is an independent business and a customer of MSA. This
              Agreement does not create any partnership, joint venture, agency,
              or employment relationship between MSA and Licensee. Licensee
              determines when, where, and how it operates its business and
              provides services to its clients or customers.
            </Section>

            <Section title="3. Licensee and Customer Relationships">
              End-users or clients are the Licensee’s customers, not MSA’s.
              Employees and subcontractors who operate under the Licensee’s
              business are also not affiliated with MSA. Licensee is solely
              responsible for:
              <ul className="list-disc ml-6 mt-2">
                <li>
                  Setting pricing, commissions, and collecting payments from
                  clients and subcontractors.
                </li>
                <li>Managing all disputes, complaints, and refunds.</li>
                <li>
                  Complying with all federal, state, and local laws governing
                  its operations.
                </li>
              </ul>
              <p className="mt-2">
                MSA is not responsible for the conduct of Licensee, its
                employees, its subcontractors or its customers, and assumes no
                liability for disputes, claims, or damages among them. Licensee
                agrees to indemnify and hold harmless MSA, pursuant to Part 12
                of this Agreement, from any such claims.
              </p>
            </Section>

            <Section title="4. Licensee Responsibilities">
              By using the Software, Licensee represents and warrants that it,
              its employees and its subcontractors:
              <ul className="list-disc ml-6 mt-2">
                <li>
                  Maintain all required business licenses, permits, and
                  insurance.
                </li>
                <li>
                  Maintain equipment in safe operating condition and in
                  compliance with applicable law.
                </li>
                <li>
                  Comply with all safety, traffic, and employment regulations
                  applicable to their operations.
                </li>
                <li>
                  Pay all applicable taxes and fees related to their business
                  activities.
                </li>
              </ul>
            </Section>

            <Section title="5. Prohibited Use">
              Licensees may not use the Software, or allow it to be used, for
              any unlawful, fraudulent, or harmful activity. This includes,
              without limitation:
              <ul className="list-disc ml-6 mt-2">
                <li>Transporting illegal goods or substances.</li>
                <li>
                  Operating vehicles without required licenses or insurance.
                </li>
                <li>Engaging in harassment, assault, or unsafe conduct.</li>
              </ul>
              <p className="mt-2">
                Violation of this section constitutes a material breach of this
                Agreement, which may result in the suspension or termination of
                this agreement by MSA (See Part 15 of this Agreement).
              </p>
            </Section>

            <Section title="6. Software Access and Termination of Use">
              MSA does not monitor or control Licensee’s operations and has no
              obligation to investigate its customers or its subcontractors'
              complaints. Licensee warrants that Licensee is not utilizing MSA’s
              software exclusively for the purposes of obtaining riders or
              providing rides.
              <br />
              <br />
              However, MSA may suspend or terminate Licensee’s access to the
              Software if it becomes aware of activity that is illegal, unsafe,
              fraudulent, or harmful to others, including (without limitation):
              <ul className="list-disc ml-6 mt-2">
                <li>Operating without valid licenses or insurance.</li>
                <li>Fraud or misrepresentation.</li>
                <li>Violent, threatening, or abusive behavior.</li>
              </ul>
              <p className="mt-2">
                Licensee retains discretion to refuse or terminate service to
                its end users, customers or its subcontractors for any lawful
                reason (See Part 15 of this agreement).
              </p>
            </Section>

            <Section title="7. Fees and Payments">
              Licensee will pay MSA a as stated in Exhibit A: Licensing Fees,
              billed in advance and non-refundable unless otherwise agreed in
              writing. Licensee is solely responsible for setting and collecting
              any amounts from its customers and subcontractors, including
              fares, commissions, or other fees, as well as all related taxes.
              MSA does not process or receive payments on behalf of Licensee or
              its subcontractors for services provided to their customers and
              does not act as a payment intermediary.
            </Section>

            <Section title="8. Intellectual Property">
              The Software, platform, code, trademarks, and documentation are
              the exclusive property of MSA. MSA grants Licensee a
              <strong>
                non-exclusive, non-transferable, revocable license
              </strong>{" "}
              to use the Software under its own brand for the duration of this
              Agreement.
              <br />
              <br />
              Licensee may not copy, modify, decompile, or reverse engineer the
              Software or claim any ownership interest in it.
            </Section>

            <Section title="9. Insurance Disclaimer">
              Licensee and its subcontractors are required by law to maintain
              all necessary insurance coverage. MSA does not provide or verify
              insurance and makes no warranty as to adequacy of such coverage.
            </Section>

            <Section title="10. Disclaimers">
              The Software is provided “as is,” without warranties of any kind.
              MSA does not guarantee uninterrupted or error-free operation and
              disclaims all responsibility for any accident, injury, property
              damage, or other loss arising from Licensee’s operations.
            </Section>

            <Section title="11. Limitation of Liability">
              To the maximum extent permitted by law, MSA shall not be liable
              for any indirect, incidental, special, consequential, or punitive
              damages, including loss of profits, data, or goodwill, or for
              personal injury or property damage, arising from or related to
              Licensee’s use of the Software or Licensee’s business operations.
            </Section>

            <Section title="12. Indemnification">
              Licensee agrees to indemnify, defend, and hold harmless MSA, its
              affiliates, officers, directors, employees, and agents from any
              claims, damages, losses, and expenses (including attorneys’ fees)
              arising out of:
              <ul className="list-disc ml-6 mt-2">
                <li>Licensee’s or its Subcontractors’ use of the Software.</li>
                <li>
                  Licensee’s business operations or services provided to its
                  customers.
                </li>
                <li>Any violation of this Agreement or applicable law.</li>
              </ul>
            </Section>

            <Section title="13. Dispute Resolution">
              Any dispute arising under this Agreement shall be resolved by
              binding arbitration on an individual basis, administered by the
              American Arbitration Association (AAA) under its Commercial
              Arbitration Rules. Arbitration will occur in{" "}
              <strong>Tarrant County, Texas,</strong> unless otherwise agreed.
              Class or representative actions are not permitted.
            </Section>

            <Section title="14. Governing Law">
              This Agreement is governed by and construed in accordance with the
              laws of the State of <strong>Texas</strong>, without regard to
              conflict of law principles.
            </Section>

            <Section title="15. Termination">
              This Agreement remains in effect until terminated. Licensee may
              terminate by closing its account. MSA may suspend or terminate the
              account if Licensee violates this Agreement, fails to pay fees, or
              engages in conduct deemed harmful to MSA’s reputation or business.
            </Section>

            <Section title="16. Entire Agreement">
              This Agreement constitutes the entire understanding between MSA
              and Licensee regarding use of the Software and supersedes all
              prior agreements or representations.
            </Section>
          </div>
        </div>

        {/* // */}

        {/* Checkbox section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Checkbox
              checked={authData.acceptTermsOfServices}
              onChange={handleAgreementChange}
              sx={{
                color: "#49454F",
                "&.Mui-checked": { color: "#0A0A0A" },
                "&.MuiSvgIcon-root": { width: "18px", height: "18px" },
              }}
            />
            <Typography
              sx={{
                color: "#727272",
                fontSize: "14px",
                fontFamily: "SF Pro",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              You have reviewed and agree to the{" "}
              <span style={{ color: "#0498FA" }}>Terms and Services</span>.
            </Typography>
          </Box>

          {errors?.acceptTermsOfServices && (
            <Typography
              sx={{
                color: "#B3261E",
                fontSize: "11px",
                fontFamily: "SF Pro",
                mt: -1,
              }}
            >
              {errors?.acceptTermsOfServices}
            </Typography>
          )}
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1.5 }}>
          <Button
            disabled={isLoading}
            onClick={prevStepHandler}
            sx={{
              width: "100%",
              borderRadius: "100px",
              border: "1px solid #0A0A0A",
              color: "#0A0A0A !important",
              textTransform: "none",
              fontFamily: "SF Pro",
              fontWeight: 500,
              fontSize: 13,
              lineHeight: "14px",
              paddingY: "12px",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Previous
          </Button>
          <Button
            disabled={isLoading}
            onClick={nextStepHandler}
            sx={{
              width: "100%",
              borderRadius: "100px",
              backgroundColor: "#0A0A0A",
              color: "white !important",
              textTransform: "none",
              fontFamily: "SF Pro",
              fontWeight: 500,
              fontSize: 13,
              lineHeight: "14px",
              paddingY: "12px",
              "&:hover": { backgroundColor: "#0A0A0A" },
            }}
          >
            {isLoading ? <Spinner /> : "Sign Up"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TermsAndServices;

/* Reusable Section Component */
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2 className="text-base font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-sm text-gray-700 leading-relaxed">{children}</p>
    </section>
  );
}
